/**
 * The Analyst rail's open thread, named in the URL so a reload lands back on
 * it and a thread opened from the sidebar opens the rail.
 *
 * Shared by every workflow page that hosts a rail. The page stays mounted
 * when the sidebar moves from one of its threads to another, so a newly named
 * thread has to open the rail, not only a mount that already had one in the
 * URL. The name is written from the URL as it is when the write runs, not as
 * it was at the last render: the chat reports a new thread from inside its
 * own send, and a write built on a stale copy is one the next write drops.
 * Replaced, not pushed: Back leaves the page, not the thread.
 */

import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

export interface RailThread {
	/** The thread the URL names, or null. */
	threadId: string | null;
	/** Whether the rail is open. */
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	/** Name a thread in the URL, or clear the name. */
	nameThread: (chatId: string | null) => void;
	/** Close the rail and drop the name. */
	close: () => void;
}

export function useRailThread(param: string): RailThread {
	const [searchParams, setSearchParams] = useSearchParams();
	const threadId = searchParams.get(param);
	const [open, setOpen] = useState(threadId !== null);
	useEffect(() => {
		if (threadId !== null) setOpen(true);
	}, [threadId]);
	const nameThread = useCallback(
		(chatId: string | null) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					if (chatId) next.set(param, chatId);
					else next.delete(param);
					return next;
				},
				{ replace: true },
			);
		},
		[param, setSearchParams],
	);
	const close = useCallback(() => {
		setOpen(false);
		nameThread(null);
	}, [nameThread]);
	return { threadId, open, setOpen, nameThread, close };
}
